/**
 * Tenant component contract — BYOD Phase 1.
 *
 * A tenant package registers its own React components for the page builder
 * here. The implementation stays in the tenant package; Studio stores only
 * content plus a serializable manifest describing what exists.
 *
 * Two layers, deliberately separate:
 *
 *   registration  — this file's `TenantComponentDefinition`. Holds React
 *                   render functions and (optionally) custom editor widgets.
 *                   Lives in the tenant bundle. NEVER crosses the wire.
 *   manifest      — `deriveTenantComponentManifest()`. Names, versions and
 *                   field types only. JSON-safe. This is what Studio stores
 *                   and validates published pages against.
 *
 * The manifest is derived rather than hand-written so the two cannot drift:
 * a field added to a component appears in the manifest on the next deploy
 * without anyone remembering to update a second list.
 *
 * Naming note: `TenantPageBuilderConfig` in ./pageBuilder is a different
 * thing — a per-tenant *policy* (which built-in sections are allowed, and
 * their default config). It predates this contract and is untouched. The
 * registry below is the component *implementation* map.
 */
import type { StudioPageSectionType } from "./pageBuilder";
import { STUDIO_PAGE_SECTION_TYPES } from "./pageBuilder";

/**
 * Bumped when a tenant renderer must be newer than the stored content — e.g.
 * a field type Studio may now emit that older bundles cannot render. Studio
 * refuses to publish content whose required version exceeds what the tenant's
 * deployed manifest reports.
 */
export const TENANT_COMPONENT_CONTRACT_VERSION = 1;

/**
 * Component names are stored in page content and matched at render time, so
 * they must survive JSON, URLs and log lines unchanged. PascalCase keeps them
 * visibly distinct from the built-in snake_case section types.
 */
export const TENANT_COMPONENT_NAME_PATTERN = /^[A-Z][A-Za-z0-9]{2,63}$/;

// ---------------------------------------------------------------------------
// Field definitions
// ---------------------------------------------------------------------------

/**
 * Field kinds a tenant component can declare. Every one of these is
 * expressible in the manifest, which is what makes Studio able to validate
 * stored props without loading tenant code.
 */
export type TenantFieldType =
	| "text"
	| "textarea"
	| "richtext"
	| "number"
	| "boolean"
	| "select"
	| "media"
	| "link"
	| "array"
	| "object"
	| "custom";

interface TenantFieldBase {
	/** Editor label. Falls back to a humanised key when absent. */
	label?: string;
	/** Helper text shown under the control in the editor. */
	help?: string;
	/** Editors may not leave this empty. Advisory — enforce in the renderer too. */
	required?: boolean;
	/**
	 * Content in this field is translatable, so the editor offers it per
	 * locale and it is stored in the section's `config_i18n` overlay rather
	 * than only in the base config.
	 */
	localized?: boolean;
}

export interface TenantTextField extends TenantFieldBase {
	type: "text" | "textarea" | "richtext";
	maxLength?: number;
	placeholder?: string;
}

export interface TenantNumberField extends TenantFieldBase {
	type: "number";
	min?: number;
	max?: number;
	step?: number;
}

export interface TenantBooleanField extends TenantFieldBase {
	type: "boolean";
}

export interface TenantSelectField extends TenantFieldBase {
	type: "select";
	options: ReadonlyArray<{ label: string; value: string | number | boolean }>;
}

export interface TenantMediaField extends TenantFieldBase {
	type: "media";
	/** Restrict the picker, e.g. ["image/png", "image/jpeg"]. */
	accept?: readonly string[];
}

export interface TenantLinkField extends TenantFieldBase {
	type: "link";
}

export interface TenantArrayField extends TenantFieldBase {
	type: "array";
	/** Shape of one entry. */
	of: TenantFieldMap;
	minItems?: number;
	maxItems?: number;
}

export interface TenantObjectField extends TenantFieldBase {
	type: "object";
	fields: TenantFieldMap;
}

/**
 * Escape hatch for a bespoke editor control. `render` is a React component
 * and therefore NOT serializable — the manifest records the field as
 * `"custom"` with its `valueType`, which is what Studio validates against.
 * Prefer a declared type where one fits; a custom field is opaque to Studio.
 */
export interface TenantCustomField extends TenantFieldBase {
	type: "custom";
	/** How the stored value should be validated despite the bespoke control. */
	valueType: "string" | "number" | "boolean" | "object" | "array";
	render: unknown;
}

export type TenantComponentField =
	| TenantTextField
	| TenantNumberField
	| TenantBooleanField
	| TenantSelectField
	| TenantMediaField
	| TenantLinkField
	| TenantArrayField
	| TenantObjectField
	| TenantCustomField;

export type TenantFieldMap = Record<string, TenantComponentField>;

// ---------------------------------------------------------------------------
// Component + root definitions
// ---------------------------------------------------------------------------

export interface TenantComponentDefinition<
	Props extends Record<string, unknown> = Record<string, unknown>,
> {
	/**
	 * Bumped when this component's props change shape incompatibly. Stored
	 * alongside the content so a page saved against v2 is not silently
	 * rendered by a deployed v1 bundle.
	 */
	version: number;
	/** Shown in the editor's component list. Defaults to the registry key. */
	label?: string;
	/** Groups the component in the editor's picker. */
	category?: "content" | "commerce" | "layout" | "trust";
	fields: TenantFieldMap;
	/** Seed props for a newly inserted instance. Editor placeholders, not copy. */
	defaultProps?: Partial<Props>;
	/** The React component. Never serialized. */
	render: unknown;
}

/**
 * The page shell: background, header/footer, content width, design-system
 * application, locale/direction. One per tenant.
 */
export interface TenantPageRootDefinition<
	Props extends Record<string, unknown> = Record<string, unknown>,
> {
	/** Stable identifier reported in the manifest, e.g. "GreenShieldPageRoot". */
	name: string;
	version: number;
	/** Root-level editable settings — header variant, footer visibility, etc. */
	fields?: TenantFieldMap;
	defaultProps?: Partial<Props>;
	render: unknown;
}

export interface TenantComponentRegistry {
	contractVersion: number;
	root: TenantPageRootDefinition;
	components: Record<string, TenantComponentDefinition>;
}

// ---------------------------------------------------------------------------
// Manifest (serializable — this is what Studio stores)
// ---------------------------------------------------------------------------

export interface TenantManifestField {
	type: TenantFieldType;
	required: boolean;
	localized: boolean;
	/** Present for `custom` fields, whose control Studio cannot introspect. */
	valueType?: TenantCustomField["valueType"];
	/**
	 * Present for `select`. Carried so Studio can reject a stored value that is
	 * not one of the offered choices — without it, an out-of-range option is
	 * only caught when the component renders something unexpected.
	 */
	options?: Array<string | number | boolean>;
	/** Present for `array` / `object`, describing the nested shape. */
	fields?: Record<string, TenantManifestField>;
}

export interface TenantComponentManifestEntry {
	version: number;
	label: string;
	category: string;
	fields: Record<string, TenantManifestField>;
}

export interface TenantComponentManifest {
	contractVersion: number;
	root: string;
	rootVersion: number;
	rootFields: Record<string, TenantManifestField>;
	components: Record<string, TenantComponentManifestEntry>;
}

function manifestField(field: TenantComponentField): TenantManifestField {
	const entry: TenantManifestField = {
		type: field.type,
		required: field.required === true,
		localized: field.localized === true,
	};
	if (field.type === "custom") entry.valueType = field.valueType;
	if (field.type === "select") {
		entry.options = field.options.map(option => option.value);
	}
	if (field.type === "object") entry.fields = manifestFields(field.fields);
	if (field.type === "array") entry.fields = manifestFields(field.of);
	return entry;
}

function manifestFields(fields: TenantFieldMap): Record<string, TenantManifestField> {
	const out: Record<string, TenantManifestField> = {};
	for (const [key, field] of Object.entries(fields)) out[key] = manifestField(field);
	return out;
}

/**
 * Reduce a registry to the JSON-safe manifest Studio stores. Drops every
 * React reference — call this before sending anything to Studio, and never
 * hand-maintain the result.
 */
export function deriveTenantComponentManifest(
	registry: TenantComponentRegistry,
): TenantComponentManifest {
	const components: Record<string, TenantComponentManifestEntry> = {};
	for (const [name, def] of Object.entries(registry.components)) {
		components[name] = {
			version: def.version,
			label: def.label ?? name,
			category: def.category ?? "content",
			fields: manifestFields(def.fields),
		};
	}
	return {
		contractVersion: registry.contractVersion,
		root: registry.root.name,
		rootVersion: registry.root.version,
		rootFields: manifestFields(registry.root.fields ?? {}),
		components,
	};
}

// ---------------------------------------------------------------------------
// Construction + validation
// ---------------------------------------------------------------------------

/** Identity helper that pins the Props generic without widening `fields`. */
export function defineTenantComponent<Props extends Record<string, unknown>>(
	definition: TenantComponentDefinition<Props>,
): TenantComponentDefinition<Props> {
	return definition;
}

export function defineTenantPageRoot<Props extends Record<string, unknown>>(
	definition: TenantPageRootDefinition<Props>,
): TenantPageRootDefinition<Props> {
	return definition;
}

const BUILT_IN_SECTION_TYPES: ReadonlySet<string> = new Set<string>(
	STUDIO_PAGE_SECTION_TYPES as readonly string[],
);

/**
 * Fails loudly at module load in the tenant package — a bad registry should
 * break that tenant's build, not surface as an unrenderable published page.
 *
 * Note on duplicates: a component map is a plain object, so two entries with
 * the same key cannot coexist (the later silently wins). What is checked here
 * is the collision that actually happens in practice — a tenant name that
 * shadows a built-in section type, and case-insensitive near-duplicates like
 * `Hero`/`hero`, which read as distinct in source but not to a human
 * scanning the editor's component list.
 */
/**
 * Nested `localized: true` inside a container that is NOT itself localized.
 *
 * Studio decides what a translator may override from the localized flag of
 * the TOP-LEVEL field only — `_validate_props(..., localized_only=True)` in
 * `apps/studio_core/tenant_components.py` never recurses into an array's
 * `of:` or an object's `fields:`. So a flag on a nested field is recorded in
 * the manifest and honoured by nobody: the editor hides the control in
 * translation mode, the save path drops the key, and the backend rejects it.
 * The field simply cannot be translated, and nothing says so.
 *
 * Reported, never thrown. Registries already shipping this pattern would stop
 * building, and the pattern is inert rather than dangerous — the right moment
 * to change one is when its owner is ready to republish the manifest, not
 * when someone else edits this file.
 *
 * Silent in production: the check tells a package author something at build
 * or dev time, and the registry is also constructed on every server render of
 * a published page, where the same line would be pure log noise. Same gating
 * as the unknown-component warning in the storefront's TenantComponentSection.
 */
function warnUnreachableLocalizedFields(
	registry: Omit<TenantComponentRegistry, "contractVersion"> &
		Partial<Pick<TenantComponentRegistry, "contractVersion">>,
): void {
	if (typeof process !== "undefined" && process.env?.NODE_ENV === "production") {
		return;
	}

	const findings: string[] = [];

	/**
	 * `depth` 0 is the top level, where a localized flag IS honoured, so those
	 * are never reported. Below that, a flag counts only if some ancestor
	 * container carries one — once a container is localized its whole value
	 * travels per locale, which makes nested flags redundant but harmless.
	 */
	const walk = (
		fields: TenantFieldMap,
		path: string,
		depth: number,
		ancestorLocalized: boolean,
	) => {
		for (const [key, field] of Object.entries(fields ?? {})) {
			if (!field || typeof field !== "object") continue;
			const here = `${path}.${key}`;
			const localized = field.localized === true;
			if (depth > 0 && localized && !ancestorLocalized) {
				findings.push(here);
			}
			const nested =
				field.type === "array"
					? field.of
					: field.type === "object"
						? field.fields
						: undefined;
			if (nested) {
				walk(nested, here, depth + 1, ancestorLocalized || localized);
			}
		}
	};

	if (registry.root?.fields) {
		walk(registry.root.fields, registry.root.name, 0, false);
	}
	for (const [name, def] of Object.entries(registry.components ?? {})) {
		if (def?.fields) walk(def.fields, name, 0, false);
	}

	if (findings.length) {
		// eslint-disable-next-line no-console
		console.warn(
			`[tenant-registry] These fields are marked localized inside a container that is not, ` +
				`so they cannot be translated:\n  - ${findings.join("\n  - ")}\n` +
				`Mark the containing array/object \`localized: true\` to translate it as a whole, ` +
				`or drop the flag. See docs/BYOD.md.`,
		);
	}
}

export function createTenantComponentRegistry(
	registry: Omit<TenantComponentRegistry, "contractVersion"> &
		Partial<Pick<TenantComponentRegistry, "contractVersion">>,
): TenantComponentRegistry {
	const contractVersion = registry.contractVersion ?? TENANT_COMPONENT_CONTRACT_VERSION;
	const errors: string[] = [];

	if (contractVersion > TENANT_COMPONENT_CONTRACT_VERSION) {
		errors.push(
			`contractVersion ${contractVersion} exceeds this runtime's v${TENANT_COMPONENT_CONTRACT_VERSION} — upgrade @crosure/tenant-runtime.`,
		);
	}

	if (!registry.root || typeof registry.root !== "object") {
		errors.push("root is required — the page shell component.");
	} else {
		if (!TENANT_COMPONENT_NAME_PATTERN.test(registry.root.name ?? "")) {
			errors.push(
				`root.name "${registry.root.name}" must be PascalCase, 3-64 chars.`,
			);
		}
		if (!registry.root.render) errors.push("root.render is required.");
		if (!Number.isInteger(registry.root.version) || registry.root.version < 1) {
			errors.push("root.version must be a positive integer.");
		}
	}

	const seenLower = new Map<string, string>();
	for (const [name, def] of Object.entries(registry.components ?? {})) {
		if (!TENANT_COMPONENT_NAME_PATTERN.test(name)) {
			errors.push(`component "${name}" must be PascalCase, 3-64 chars.`);
		}
		if (BUILT_IN_SECTION_TYPES.has(name.toLowerCase())) {
			errors.push(
				`component "${name}" collides with the built-in section type "${name.toLowerCase()}".`,
			);
		}
		const prior = seenLower.get(name.toLowerCase());
		if (prior) {
			errors.push(`components "${prior}" and "${name}" differ only by case.`);
		} else {
			seenLower.set(name.toLowerCase(), name);
		}
		if (!def || typeof def !== "object") {
			errors.push(`component "${name}" must be an object.`);
			continue;
		}
		if (!def.render) errors.push(`component "${name}" is missing render.`);
		if (!Number.isInteger(def.version) || def.version < 1) {
			errors.push(`component "${name}".version must be a positive integer.`);
		}
		if (!def.fields || typeof def.fields !== "object") {
			errors.push(`component "${name}" must declare a fields map.`);
			continue;
		}
		// A defaultProp with no matching field is dead weight: the editor shows
		// no control for it, so nobody can ever change the value it seeds.
		for (const key of Object.keys(def.defaultProps ?? {})) {
			if (!(key in def.fields)) {
				errors.push(
					`component "${name}" has defaultProps.${key} with no matching field.`,
				);
			}
		}
		for (const [key, field] of Object.entries(def.fields)) {
			if (!field || typeof field !== "object" || !("type" in field)) {
				errors.push(`component "${name}".fields.${key} needs a type.`);
				continue;
			}
			if (field.type === "select" && !(field.options?.length > 0)) {
				errors.push(`component "${name}".fields.${key} (select) needs options.`);
			}
			if (field.type === "custom" && !field.render) {
				errors.push(`component "${name}".fields.${key} (custom) needs render.`);
			}
		}
	}

	if (errors.length) {
		throw new Error(
			`Invalid tenant component registry:\n  - ${errors.join("\n  - ")}`,
		);
	}

	warnUnreachableLocalizedFields(registry);

	return { contractVersion, root: registry.root, components: registry.components };
}

/**
 * Studio-side guard: is this stored section renderable by the manifest the
 * tenant actually has deployed? Returns the reasons it is not, so a publish
 * check can report all of them at once rather than the first.
 */
export function validateAgainstManifest(
	manifest: TenantComponentManifest,
	section: { component: string; version?: number; props?: Record<string, unknown> },
): string[] {
	const problems: string[] = [];
	const entry = manifest.components[section.component];
	if (!entry) {
		problems.push(`Component "${section.component}" is not in the deployed manifest.`);
		return problems;
	}
	if (section.version != null && section.version > entry.version) {
		problems.push(
			`Content targets "${section.component}" v${section.version}; deployed bundle has v${entry.version}.`,
		);
	}
	for (const key of Object.keys(section.props ?? {})) {
		if (!(key in entry.fields)) {
			problems.push(`Unknown prop "${key}" for "${section.component}".`);
		}
	}
	for (const [key, field] of Object.entries(entry.fields)) {
		if (field.required && section.props?.[key] == null) {
			problems.push(`Missing required prop "${key}" for "${section.component}".`);
		}
	}
	return problems;
}

export type { StudioPageSectionType };
